/**
 * Device Colors Utility
 * Centraliza a lógica de cores baseada em status e movimento dos dispositivos
 */

import { alpha } from '@mui/material/styles';

// Constantes de altura do DeviceRow (v2: Layout simplificado)
// 2ECOMP: height(72) + borderBottom(1) = 73px
export const DEVICE_ROW_HEIGHT = 73;

// 2ECOMP Premium: Status configuration - Padrão Traccar + Moving azul
// Cores alinhadas com Traccar oficial para familiaridade do usuário
export const STATUS_CONFIG = {
  moving: {
    label: 'Movimento',
    color: '#3b82f6',           // Azul (customização 2ECOMP)
    bgColor: 'rgba(59, 130, 246, 0.12)',
    borderColor: 'rgba(59, 130, 246, 0.5)',
  },
  online: {
    label: 'Online',
    color: '#4caf50',           // Verde (padrão Traccar)
    bgColor: 'rgba(76, 175, 80, 0.12)',
    borderColor: 'rgba(76, 175, 80, 0.5)',
  },
  stopped: {
    label: 'Parado',
    color: '#d29922',           // Âmbar
    bgColor: 'rgba(210, 153, 34, 0.12)',
    borderColor: 'rgba(210, 153, 34, 0.5)',
  },
  offline: {
    label: 'Offline',
    color: '#f44336',           // Vermelho (padrão Traccar)
    bgColor: 'rgba(244, 67, 54, 0.12)',
    borderColor: 'rgba(244, 67, 54, 0.5)',
  },
  unknown: {
    label: 'Ocioso',
    color: '#9e9e9e',           // Cinza (padrão Traccar)
    bgColor: 'rgba(158, 158, 158, 0.12)',
    borderColor: 'rgba(158, 158, 158, 0.5)',
  },
};

/**
 * Determina se um dispositivo está realmente em movimento
 * Considera múltiplos critérios para maior precisão
 * @param {Object} position - Objeto de posição
 * @param {number} minSpeed - Velocidade mínima em knots para considerar em movimento (default: 1)
 * @param {number} maxAgeMinutes - Idade máxima da posição em minutos (default: 10)
 * @returns {boolean} true se está em movimento
 */
export const isDeviceMoving = (position, minSpeed = 1, maxAgeMinutes = 10) => {
  if (!position) return false;

  const attrs = position.attributes || {};

  // Critério 1: Atributo motion explícito
  const motionAttr = attrs.motion;

  // Critério 2: Velocidade acima do mínimo (em knots)
  const speed = position.speed || 0;
  const hasSpeed = speed >= minSpeed;

  // Critério 3: Ignição ligada (alguns rastreadores)
  const ignitionOn = attrs.ignition === true;

  // Critério 4: Posição recente (não muito antiga)
  const fixTime = position.fixTime ? new Date(position.fixTime) : null;
  const isRecent = fixTime ? (Date.now() - fixTime.getTime()) < (maxAgeMinutes * 60 * 1000) : false;

  // Lógica de decisão:
  // - Se motion é explicitamente true E posição é recente, está em movimento
  // - Se motion não está definido, usar velocidade + posição recente
  // - Se velocidade > mínimo E posição recente, está em movimento
  if (motionAttr === true && isRecent) return true;
  if (motionAttr === false) return false;

  // Fallback: velocidade + recência
  return hasSpeed && isRecent;
};

/**
 * Determina o status efetivo baseado no status do device e movimento
 * @param {string} deviceStatus - Status do device (online, offline, unknown)
 * @param {boolean|undefined} isMoving - Se está em movimento (position.attributes.motion)
 * @param {boolean} hasPosition - Se tem dados de posição
 * @returns {string} Status efetivo (moving, online, stopped, offline, unknown)
 */
export const getEffectiveStatus = (deviceStatus, isMoving, hasPosition = false) => {
  if (deviceStatus === 'offline') return 'offline';
  if (deviceStatus === 'online') {
    if (isMoving === true) return 'moving';
    if (hasPosition && isMoving === false) return 'stopped';
    return 'online';
  }
  return 'unknown';
};

/**
 * Versão avançada que usa a função isDeviceMoving
 * @param {string} deviceStatus - Status do device
 * @param {Object} position - Objeto de posição completo
 * @returns {string} Status efetivo
 */
export const getEffectiveStatusAdvanced = (deviceStatus, position) => {
  if (deviceStatus === 'offline') return 'offline';
  if (deviceStatus === 'online') {
    if (!position) return 'online';
    if (isDeviceMoving(position)) return 'moving';
    return 'stopped';
  }
  return 'unknown';
};

// Thresholds de velocidade para indicadores
export const SPEED_THRESHOLDS = {
  MAX: 50,    // Velocidade alta (vermelho)
  HIGH: 25,   // Velocidade média-alta (verde)
  LOW: 0,     // Velocidade baixa (verde claro)
};

// Thresholds de bateria
export const BATTERY_THRESHOLDS = {
  CRITICAL: 20,
  LOW: 30,
  MEDIUM: 50,
  HIGH: 80,
  FULL: 100,
};

// Mapeamento de status para chaves de cor do tema
export const STATUS_COLOR_MAP = {
  moving: 'info',
  online: 'success',
  offline: 'error',
  unknown: 'warning',
};

/**
 * Determina a chave de cor baseada no status e movimento
 * @param {string} status - Status do device (online, offline, unknown)
 * @param {boolean} isMoving - Se o device está em movimento
 * @returns {string} Chave de cor do tema
 */
export const getStatusColorKey = (status, isMoving = false) => {
  if (status === 'online' && isMoving) {
    return STATUS_COLOR_MAP.moving;
  }
  return STATUS_COLOR_MAP[status] || STATUS_COLOR_MAP.unknown;
};

/**
 * Calcula as cores de borda e fundo para um device
 * @param {Object} params - Parâmetros
 * @param {string} params.status - Status do device
 * @param {boolean} params.isMoving - Se está em movimento
 * @param {boolean} params.isSelected - Se está selecionado
 * @param {boolean} params.isDark - Se está no modo escuro
 * @param {Object} params.palette - Paleta de cores do tema
 * @returns {Object} { borderColor, backgroundColor }
 */
export const getDeviceColors = ({ status, isMoving, isSelected, isDark, palette }) => {
  const colorKey = getStatusColorKey(status, isMoving);
  const paletteColor = palette[colorKey];

  if (!paletteColor) {
    return {
      borderColor: palette.grey[500],
      backgroundColor: alpha(palette.grey[500], 0.12),
    };
  }

  if (isSelected) {
    return {
      borderColor: paletteColor.main,
      backgroundColor: alpha(paletteColor.main, 0.3),
    };
  }

  return {
    borderColor: isDark
      ? alpha(paletteColor.light || paletteColor.main, 0.8)
      : alpha(paletteColor.dark || paletteColor.main, 0.8),
    backgroundColor: isDark
      ? alpha(paletteColor.dark || paletteColor.main, 0.12)
      : alpha(paletteColor.light || paletteColor.main, 0.12),
  };
};

/**
 * Determina a classe de cor para o nível de bateria
 * @param {number} level - Nível de bateria (0-100)
 * @param {boolean} isCharging - Se está carregando
 * @returns {string} Nome da classe de cor (success, warning, error, info)
 */
export const getBatteryColorClass = (level, isCharging = false) => {
  if (level >= BATTERY_THRESHOLDS.FULL && isCharging) {
    return 'info';
  }
  if (level < BATTERY_THRESHOLDS.LOW) {
    return 'error';
  }
  if (level < BATTERY_THRESHOLDS.MEDIUM) {
    return 'warning';
  }
  return 'success';
};

/**
 * Determina a classe de cor para a velocidade
 * @param {number} speed - Velocidade em knots
 * @returns {string} Nome da classe de cor
 */
export const getSpeedColorClass = (speed) => {
  if (speed > SPEED_THRESHOLDS.MAX) {
    return 'error';
  }
  if (speed > SPEED_THRESHOLDS.HIGH) {
    return 'success';
  }
  if (speed > SPEED_THRESHOLDS.LOW) {
    return 'success';
  }
  return 'warning';
};

/**
 * Normaliza o nível de bateria para um valor entre 0 e 100
 * @param {number|string|null|undefined} value - Valor da bateria
 * @returns {number} Valor normalizado entre 0 e 100
 */
export const normalizeBatteryLevel = (value) => {
  const num = Number(value) || 0;
  return Math.min(100, Math.max(0, Math.floor(num)));
};

/**
 * Obtém o valor de tensão de alimentação do dispositivo
 * @param {Object} attributes - Atributos da posição
 * @returns {string} Valor formatado com 1 casa decimal
 */
export const getPowerVoltage = (attributes = {}) => {
  const value = attributes.power
    ?? attributes.voltage
    ?? attributes.battery
    ?? attributes.adc1
    ?? 0;
  return (Number(value) || 0).toFixed(1);
};
